/**
 * Подрядчики графика (ПЛ-32), вид генподрядчика: по каждому подрядчику — выполнение, окончание против
 * основного базового плана, вклад его работ в перенос окончания проекта; отчёты подрядчиков о выполнении —
 * принять в график или отклонить с причиной.
 */
import {
  CONTRACTOR_UPDATE_STATUSES,
  type ContractorsReport,
  type ContractorUpdateRow,
  type ScheduleView,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { scheduleKey } from './useSchedule';

const dd = (s: string | null) => (s ? s.slice(0, 10).split('-').reverse().join('.') : '—');
const n1 = (x: number | null) => (x == null ? '—' : String(Math.round(x * 10) / 10).replace('.', ','));
const shift = (d: number | null) => (d == null ? '—' : d > 0 ? `+${d} дн` : d < 0 ? `${d} дн` : 'в срок');

export function ContractorsPanel({ view }: { view: ScheduleView }) {
  const qc = useQueryClient();
  const id = view.schedule.id;
  const rep = useQuery({
    queryKey: ['contractors', id, view.schedule.revision],
    queryFn: () => api<ContractorsReport>(`/schedules/${id}/contractors`),
  });
  const ups = useQuery({
    queryKey: ['contractor-updates', id],
    queryFn: () => api<ContractorUpdateRow[]>(`/schedules/${id}/contractor-updates`),
  });
  const [showAll, setShowAll] = useState(false);
  const hist = useQuery({
    queryKey: ['contractor-updates', id, 'all'],
    queryFn: () => api<ContractorUpdateRow[]>(`/schedules/${id}/contractor-updates`, { query: { all: '1' } }),
    enabled: showAll,
  });
  const [open, setOpen] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const decide = useMutation({
    mutationFn: (x: { id: string; accept: boolean }) =>
      api(`/contractor-updates/${x.id}/decide`, { body: { accept: x.accept, note: note[x.id] || null } }),
    onSuccess: (r, x) => {
      toast(
        (r as { superseded?: boolean } | null)?.superseded
          ? 'Работа передана другому подрядчику — отчёт прежнего снят'
          : x.accept
            ? 'Отчёт принят в график'
            : 'Отчёт отклонён — подрядчик увидит причину',
      );
      void qc.invalidateQueries({ queryKey: ['contractor-updates', id] });
      void qc.invalidateQueries({ queryKey: ['contractors', id] });
      if (x.accept) void qc.invalidateQueries({ queryKey: scheduleKey(view.schedule.projectId) });
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const edit = view.can.edit;
  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Отчёты подрядчиков о выполнении"
        meta={ups.data ? `ждут проверки: ${ups.data.length}` : undefined}
      >
        {ups.isPending ? <Spinner /> : null}
        {ups.isError ? <ErrorBox error={ups.error} /> : null}
        {ups.data && !ups.data.length ? (
          <p className="text-[14px] text-ink-2">
            Новых отчётов нет. Подрядчик отмечает факт своих работ в кабинете «Мой график» — отчёт появится
            здесь.
          </p>
        ) : null}
        <ul className="divide-y divide-line text-[14px]">
          {(ups.data ?? []).map((u) => (
            <li key={u.id} className="flex flex-col gap-2 py-2">
              <div>
                <span className="font-medium">
                  {u.activityCode} {u.activityName}
                </span>
                <span className="block text-[12px] text-ink-2">
                  {u.contractorName}
                  {u.submittedBy ? ` · ${u.submittedBy}` : ''} · {dd(u.at)}
                </span>
              </div>
              <div className="text-[13px]">
                {u.actualStart ? (
                  <div>
                    Начало: <b>{dd(u.actualStart)}</b>
                    {u.current.actualStart ? ` (в графике ${dd(u.current.actualStart)})` : ''}
                  </div>
                ) : null}
                {u.actualFinish ? (
                  <div>
                    Окончание: <b>{dd(u.actualFinish)}</b>
                  </div>
                ) : null}
                {u.percent != null ? (
                  <div>
                    Выполнено: <b>{n1(u.percent)} %</b> (в графике {n1(u.current.percent)} %)
                  </div>
                ) : null}
                {u.remainingDays != null && !u.actualFinish ? (
                  <div>
                    Осталось: <b>{n1(u.remainingDays)} раб. дн</b>
                  </div>
                ) : null}
                {u.actualQuantity != null ? (
                  <div>
                    Объём: <b>{n1(u.actualQuantity)}</b>
                    {u.current.actualQuantity != null ? ` (в графике ${n1(u.current.actualQuantity)})` : ''}
                  </div>
                ) : null}
                {u.comment ? <div className="text-ink-2">«{u.comment}»</div> : null}
              </div>
              {edit ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate({ id: u.id, accept: true })}
                    className="max-md:h-11"
                  >
                    Принять в график
                  </Button>
                  <Input
                    value={note[u.id] ?? ''}
                    onChange={(e) => setNote({ ...note, [u.id]: e.target.value })}
                    placeholder="Причина отказа"
                    aria-label="Причина отказа"
                    className="w-56 max-w-full max-md:h-11"
                  />
                  <Button
                    size="sm"
                    disabled={decide.isPending || !(note[u.id] ?? '').trim()}
                    onClick={() => decide.mutate({ id: u.id, accept: false })}
                    className="max-md:h-11"
                  >
                    Отклонить
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </Panel>
      <Panel title="Рассмотренные отчёты">
        <Button size="sm" onClick={() => setShowAll(!showAll)} className="max-md:h-11">
          {showAll ? 'Скрыть' : 'Показать решения по отчётам'}
        </Button>
        {showAll && hist.isPending ? <Spinner /> : null}
        {showAll && hist.data ? (
          <ul className="mt-2 divide-y divide-line text-[13px]">
            {hist.data
              .filter((u) => u.status !== 'pending')
              .slice(0, 100)
              .map((u) => (
                <li key={u.id} className="py-1.5">
                  {dd(u.at)} · {u.activityCode} · {u.contractorName} —{' '}
                  <span className={clsx(u.status === 'rejected' && 'text-danger')}>
                    {CONTRACTOR_UPDATE_STATUSES[u.status]}
                  </span>
                  {u.decidedBy ? ` (${u.decidedBy}${u.decidedAt ? `, ${dd(u.decidedAt)}` : ''})` : ''}
                  {u.decisionNote ? `: ${u.decisionNote}` : ''}
                </li>
              ))}
          </ul>
        ) : null}
      </Panel>
      <Panel
        title="Подрядчики"
        meta={
          rep.data?.baseline
            ? `против базового плана «${rep.data.baseline.name}»`
            : 'нет основного базового плана'
        }
      >
        {rep.isPending ? <Spinner /> : null}
        {rep.isError ? <ErrorBox error={rep.error} /> : null}
        {rep.data && !rep.data.contractors.length ? (
          <EmptyState title="У работ графика нет подрядчиков">
            Укажите подрядчика в карточке работы — он увидит свои работы в кабинете, если его участник связан
            с организацией.
          </EmptyState>
        ) : null}
        {rep.data && !rep.data.impactReady ? (
          <p className="mb-2 text-[13px] text-ink-2">
            Вклад в перенос сейчас не посчитан — идут другие расчёты графика. Обновите страницу через минуту.
          </p>
        ) : null}
        <ul className="divide-y divide-line text-[14px]">
          {(rep.data?.contractors ?? []).map((c) => (
            <li key={c.id} className="py-2">
              <button
                type="button"
                onClick={() => setOpen(open === c.id ? null : c.id)}
                aria-expanded={open === c.id}
                className="flex min-h-11 w-full flex-wrap items-center justify-between gap-2 text-left md:min-h-0"
              >
                <span>
                  <span className="font-medium">{c.name}</span>
                  <span className="block text-[12px] text-ink-2">
                    работ {c.total} · выполнено {c.done} · идут {c.inProgress} · {n1(c.percent)} %
                    {c.members ? '' : ' · нет участника в портале'}
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-1">
                  {c.pending ? <Badge tone="info">отчётов: {c.pending}</Badge> : null}
                  <Badge tone={(c.varianceDays ?? 0) > 0 ? 'danger' : undefined}>
                    окончание {dd(c.finish)} · {shift(c.varianceDays)}
                  </Badge>
                  {c.impactDays ? (
                    <Badge tone="danger">вклад в перенос {n1(c.impactDays)} раб. дн</Badge>
                  ) : null}
                </span>
              </button>
              {open === c.id ? (
                <ScrollX>
                  <table className="mt-2 w-full min-w-[640px] text-[13px]">
                    <thead>
                      <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                        <th className="py-2 pr-2 pl-3">Работа</th>
                        <th className="py-2 pr-2">Окончание</th>
                        <th className="py-2 pr-2">По базовому</th>
                        <th className="py-2 pr-2 text-right">Отклонение</th>
                        <th className="py-2 pr-3 text-right">Вклад в перенос</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.activities.map((a) => (
                        <tr key={a.id} className="border-t border-line align-top">
                          <td className="py-1.5 pr-2 pl-3">
                            {a.code} {a.name}
                            {a.reasons.length ? (
                              <div className="text-[12px] text-ink-2">{a.reasons.join('; ')}</div>
                            ) : null}
                          </td>
                          <td className="py-1.5 pr-2">
                            {dd(a.finish)}
                            {a.status === 'done'
                              ? ' · выполнена'
                              : a.status === 'in_progress'
                                ? ' · идёт'
                                : ''}
                          </td>
                          <td className="py-1.5 pr-2">{dd(a.blFinish)}</td>
                          <td
                            className={clsx(
                              'py-1.5 pr-2 text-right',
                              (a.varianceDays ?? 0) > 0 && 'text-danger',
                            )}
                          >
                            {shift(a.varianceDays)}
                          </td>
                          <td
                            className={clsx(
                              'py-1.5 pr-3 text-right',
                              (a.impactDays ?? 0) > 0 && 'text-danger',
                            )}
                          >
                            {a.impactDays == null ? '—' : `${n1(a.impactDays)} раб. дн`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollX>
              ) : null}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}
